import { Injectable } from '@angular/core';
import { StorageVariable } from './../object/local-storage-variable.enum';

import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import gql from 'graphql-tag';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TipoMensaje } from 'src/Wheel.Styles.HighVolume/Mensaje';
import { NotificacionGenerica } from 'src/Wheel.Styles.HighVolume/Notificacion';
import { Login } from '../object/login.interface';
import { BloqueoModuloService } from './bloqueo-modulo.service';
import { DatesService } from './dates.service';
import { GraphQLClient } from './graph-qlclient.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private API_URL = environment.API_REST_URL;
  private ENDPOINT_BASE = `${this.API_URL}/auth`;
  // Offset de refresco (cuánto antes de la expiración intentamos refrescar)
  private readonly REFRESH_OFFSET_MS = 5 * 60 * 1000; // 5 minutos
  // Keep-alive para evitar expiración por inactividad en capas intermedias (proxy/WAF/etc.)
  private readonly KEEP_ALIVE_INTERVAL_MS = 2 * 60 * 1000; // 2 minutos

  public urlUsuarioIntentaAcceder = '';
  public changeLoginStatusSubject = new BehaviorSubject<boolean>(this.hasValidAccessToken());
  public changeLoginStatus$ = this.changeLoginStatusSubject.asObservable();

  private currentUserRut: string | null = null;
  private refreshTokenTimeout: any;
  private isRefreshingToken = false;
  // private tokenRefreshed$ = new BehaviorSubject<boolean>(false);
  private inflightRefresh$: Observable<Login | null> | null = null;
  private keepAliveInterval: any;

  constructor(
    private bloqueoVistaService: BloqueoModuloService,
    private dateService: DatesService,
    private client: GraphQLClient,
    private http: HttpClient,
    private router: Router
  ) {
    this.storeDataClient();
    this.currentUserRut = this.getRutUsuario();
    this.changeLoginStatus$.subscribe(isLoggedIn => {
      if (isLoggedIn) {
        this.scheduleRefreshToken();
        this.startKeepAlive();
      } else {
        this.clearRefreshTokenTimer();
        this.stopKeepAlive();
      }
    });
  }

  // --- Métodos de Almacenamiento ---
  getAccessToken(): string | null {
    return sessionStorage.getItem(StorageVariable.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return sessionStorage.getItem(StorageVariable.REFRESH_TOKEN_KEY);
  }

  getIp(): string | null {
    return sessionStorage.getItem(StorageVariable.IP);
  }
  getRutUsuario(): string | null {
    return localStorage.getItem(StorageVariable.RUT_USUARIO);
  }

  getUserAgent(): string {
    return navigator.userAgent;
  }

  /**
   * Inicia sesión del usuario
   * @param loginResponse datos del usuario
   */
  login(loginResponse: Login): void {
    this.currentUserRut = loginResponse.rut_usuario.toString();
    this.storeTokens(loginResponse);
    this.bloqueoVistaService.obtenerRutUsuario();
    this.changeLoginStatusSubject.next(true);
    this.scheduleRefreshToken();
  }

  /**
   * Cierra la sesión del usuario
  */
  logout() {
    const rut = this.currentUserRut;
    const refreshToken = this.getRefreshToken();

    console.log('rut', rut);
    console.log('refreshToken', refreshToken);
    
    this.bloqueoVistaService.liberarTodosLosBloqueosDelUsuarioActual().pipe(
      finalize(() => {
        if (rut && refreshToken) {
          const inputParams = {
            rut_usuario: Number(rut ? rut : '-1'),
            refresh_token: refreshToken,
          }
          this.cerrarSesion(inputParams);
        }else{
          this.clearSessionData();
          this.changeLoginStatusSubject.next(false);
          this.router.navigate(['/login'])
        }
      })
    ).subscribe();
  }

  /**
   * Cierra la sesión del usuario (sendBeacon)
   */
  logoutSendBeacon(){
    const rut = this.currentUserRut;
    const refreshToken = this.getRefreshToken();

    try {
      this.bloqueoVistaService.liberarTodosLosBloqueosDelUsuarioActual(true).subscribe();
    } catch (e) {
      console.error('Error al liberar bloqueos:', e);
    }
    this.cerrarSesionSendBeacon({ rut_usuario: rut, refresh_token: refreshToken });
  }

  /**
   * Verifica si el usuario está logueado
   * @returns true si el usuario está logueado, false en caso contrario
   */
  isLoggedIn(): boolean {
    return this.hasValidAccessToken();
  }

  /**
   * Intenta refrescar el token
   * @returns Observable con el token refrescado
   */
  tryRefreshToken(): Observable<Login | null> {
    // Si ya hay un refresco haciendose, espera a que termine
    console.log(`${this.dateService.getDateNowConsole()}: tryRefreshToken - Refreshing: ${this.isRefreshingToken}`);
    console.log(`${this.dateService.getDateNowConsole()}: tryRefreshToken - Inflight: ${this.inflightRefresh$}`);

    if (this.inflightRefresh$) return this.inflightRefresh$;

    const rut = this.currentUserRut;
    const refreshToken = this.getRefreshToken();
    console.log(`${this.dateService.getDateNowConsole()}: tryRefreshToken - RUT: ${rut} - REFRESH_TOKEN: ${refreshToken}`);

    if (!rut || !refreshToken) {
      this.isRefreshingToken = false;
      return throwError(() => new Error('No hay rut o refresh token para refrescar.'));
    }

    // Evitar golpear backend si estamos fuera de la ventana de refresco,
    // salvo que sea un keep-alive explícito.
    const force = false;
    if (!force && !this.isWithinRefreshWindow()) {
      console.log(`${this.dateService.getDateNowConsole()}: tryRefreshToken - Fuera de ventana de refresco. No se llama a backend.`);
      return of(null);
    }

    const inputParams = {
      rut_usuario: Number(rut),
      refresh_token: refreshToken,
      ip_address: this.getIp()!,
      user_agent: this.getUserAgent()
    };
    this.isRefreshingToken = true;
    // Retornar siempre un Observable que envuelva la operación asíncrona basada en callback
    this.inflightRefresh$ = new Observable<Login | null>((observer) => {
      this.client.query({
        query: gql`
        query ($rut_usuario:Decimal, $refresh_token:String, $ip_address:String, $user_agent:String)
        {
          login
          {
            refrescarToken(rut_usuario:$rut_usuario, refresh_token: $refresh_token, ip_address: $ip_address, user_agent: $user_agent)
            {
              mensajes
              {
                codigo
                contexto
                texto
                tipo
              }
              respuesta 
              {
                access_token
                refresh_token
                access_token_expiration
              }
            }
          }
        }
        `,
        variables: inputParams
      }, (response: any) => {
        try {
          this.isRefreshingToken = false;
          const notificacion: NotificacionGenerica<Login> = response.data.login.refrescarToken;
          const ok = notificacion.mensajes.some(mensaje => mensaje.tipo === TipoMensaje.SUCCESS) && notificacion.respuesta;
          if (ok) {
            this.storeTokens(notificacion.respuesta);
            this.scheduleRefreshToken();
            this.changeLoginStatusSubject.next(true);
            console.log(`${this.dateService.getDateNowConsole()}: tryRefreshToken if - Token refrescado exitosamente`, notificacion.respuesta);
            observer.next(notificacion.respuesta);
            observer.complete();
          } else {
            console.error(`${this.dateService.getDateNowConsole()}: tryRefreshToken else - Fallo al refrescar token: ${notificacion.mensajes[0].texto}`);
            // Mantener el comportamiento actual de cerrar sesión cuando falla el refresh
            const msg = notificacion && notificacion.mensajes ? notificacion.mensajes[0].texto : 'Fallo al refrescar token';
            this.logout();
            observer.error(new Error(msg));
          }
        } catch (error) {
          console.error(`${this.dateService.getDateNowConsole()}: tryRefreshToken catch - Excepción procesando refresh`, error);
          this.logout();
          observer.error(error);
        }
      });
    }).pipe(
      finalize(() => {
        this.isRefreshingToken = false;
        this.inflightRefresh$ = null;
      }),
      shareReplay(1),
    );
    return this.inflightRefresh$;
    // this.client.query({
    //   query: gql`
    //   query ($rut_usuario:Decimal, $refresh_token:String, $ip_address:String, $user_agent:String)
    //   {
    //     login
    //     {
    //       refrescarToken(rut_usuario:$rut_usuario, refresh_token: $refresh_token, ip_address: $ip_address, user_agent: $user_agent)
    //       {
    //         mensajes
    //         {
    //           codigo
    //           contexto
    //           texto
    //           tipo
    //         }
    //         respuesta 
    //         {
    //           access_token
    //           refresh_token
    //           access_token_expiration
    //         }
    //       }
    //     }
    //   }
    //   `,
    //   variables: inputParams
    // }, (response: any) => {
    //   const notificacion: NotificacionGenerica<Login> = response.data.login.refrescarToken;
    //   if (notificacion.mensajes.some(mensaje => mensaje.tipo === TipoMensaje.SUCCESS && notificacion.respuesta)) {
    //     this.storeTokens(notificacion.respuesta);
    //     this.scheduleRefreshToken();
    //     this.changeLoginStatusSubject.next(true);
    //     this.isRefreshingToken = false;
    //     this.tokenRefreshed$.next(true);
    //     this.tokenRefreshed$.next(false);
    //     console.log('Token refrescado exitosamente');
    //   }else{
    //     console.error('Fallo al refrescar token:', notificacion.mensajes[0].texto);
    //     this.logout();
    //   }
    // })
  }

  /**
   * Keep-alive periódico para evitar cierre de sesión por inactividad.
   * Llama al refresh aun fuera de ventana (no cambia el access_token si backend no lo renueva).
   */
  private startKeepAlive() {
    if (this.keepAliveInterval) return;
    this.keepAliveInterval = setInterval(() => {
      if (!this.isLoggedIn()) return;
      if (document.visibilityState !== 'visible') return;
      this.tryRefreshTokenForce().subscribe({
        next: () => {},
        error: () => {},
      });
    }, this.KEEP_ALIVE_INTERVAL_MS);
  }

  private stopKeepAlive() {
    if (!this.keepAliveInterval) return;
    clearInterval(this.keepAliveInterval);
    this.keepAliveInterval = null;
  }

  private tryRefreshTokenForce(): Observable<Login | null> {
    // Mismo flujo que tryRefreshToken pero sin cortar por ventana de refresco
    if (this.inflightRefresh$) return this.inflightRefresh$;

    const rut = this.currentUserRut;
    const refreshToken = this.getRefreshToken();
    if (!rut || !refreshToken) {
      return of(null);
    }

    const ip = this.getIp();
    if (!ip) {
      // Asegura que se intente recuperar IP para próximas ejecuciones
      this.storeDataClient();
      return of(null);
    }

    const inputParams = {
      rut_usuario: Number(rut),
      refresh_token: refreshToken,
      ip_address: ip,
      user_agent: this.getUserAgent()
    };

    this.isRefreshingToken = true;
    this.inflightRefresh$ = new Observable<Login | null>((observer) => {
      this.client.query({
        query: gql`
        query ($rut_usuario:Decimal, $refresh_token:String, $ip_address:String, $user_agent:String)
        {
          login
          {
            refrescarToken(rut_usuario:$rut_usuario, refresh_token: $refresh_token, ip_address: $ip_address, user_agent: $user_agent)
            {
              mensajes
              {
                codigo
                contexto
                texto
                tipo
              }
              respuesta 
              {
                access_token
                refresh_token
                access_token_expiration
              }
            }
          }
        }
        `,
        variables: inputParams
      }, (response: any) => {
        try {
          this.isRefreshingToken = false;
          const notificacion: NotificacionGenerica<Login> = response.data.login.refrescarToken;
          const ok = notificacion.mensajes.some(mensaje => mensaje.tipo === TipoMensaje.SUCCESS) && notificacion.respuesta;
          if (ok) {
            this.storeTokens(notificacion.respuesta);
            this.scheduleRefreshToken();
            this.changeLoginStatusSubject.next(true);
            observer.next(notificacion.respuesta);
            observer.complete();
          } else {
            const msg = notificacion && notificacion.mensajes ? notificacion.mensajes[0].texto : 'Fallo al refrescar token';
            this.logout();
            observer.error(new Error(msg));
          }
        } catch (error) {
          this.logout();
          observer.error(error);
        }
      });
    }).pipe(
      finalize(() => {
        this.isRefreshingToken = false;
        this.inflightRefresh$ = null;
      }),
      shareReplay(1),
    );

    return this.inflightRefresh$;
  }

  // NO ESTA IMPLEMENTADO, PERO SERVIRÍA PARA CERRAR SESION CUANDO SE RECIBA UN 401
  handleUnauthorized() {
    console.warn('Manejando no autorizado: cerrando sesión localmente.');
    this.logout();
  }

  cerrarSesion(inputParams:{
    rut_usuario: number, 
    refresh_token: string
  }) {
    this.client.query({
      query: gql`
      query ($rut_usuario:Decimal, $refresh_token:String)
      {
        login
        {
          cerrarSesion(rut_usuario:$rut_usuario, refresh_token: $refresh_token)
          {
            mensajes
            {
              codigo
              contexto
              texto
              tipo
            }
            respuesta 
            {
              access_token
              refresh_token
              access_token_expiration
            }
          }
        }
      }
      `,
      variables: inputParams,
      fetchPolicy: 'no-cache'
    }, ({respuesta}) => {
      this.clearSessionData();
      this.changeLoginStatusSubject.next(false);
      this.router.navigate(['/login'])
    })
  }

  /**
   * Cierra la sesión del usuario (sendBeacon)
   * @param params datos del usuario
   */
  private cerrarSesionSendBeacon(params: { rut_usuario: string, refresh_token: string }): void {
    const blob = new Blob([JSON.stringify(params)], { type: 'text/plain; charset=UTF-8' });
    const ok = navigator.sendBeacon(`${this.ENDPOINT_BASE}/cerrarSesion`, blob);
    // Como sendBeacon es fire-and-forget, no hay respuesta sincrónica.
    if (ok) {
      console.log('Cerrar sesion (sendBeacon): ', params);
      this.clearSessionData();
      this.changeLoginStatusSubject.next(false);
      // this.router.navigate(['/login'])
    }
  }

  /**
   * Verifica si el token es válido
   * @returns true si el token es válido, false en caso contrario
   */
  private hasValidAccessToken(): boolean {
    const token = this.getAccessToken();
    const expiration = this.getAccessTokenExpiration();
    console.log(`${this.dateService.getDateNowConsole()}: hasValidAccessToken: STEP 1 | EXISTE TOKEN ${(!!token)} | ${new Date(expiration)} `);
    if (!token || !expiration) return false;
  
    const now = Date.now();
    const exp = new Date(expiration).getTime();
    const skewMs = 30 * 1000;
    console.log(`${this.dateService.getDateNowConsole()}: hasValidAccessToken: STEP 2 | ACTUAL: ${new Date(now + skewMs)} | EXPIRACION: ${new Date(exp)} | VALIDO: ${now + skewMs < exp}`);
    return now + skewMs < exp;
  }
  // private hasValidAccessToken(): boolean {
  //   const token = this.getAccessToken();
  //   const expiration = this.getAccessTokenExpiration();
  //   if (token && expiration) {
  //     return new Date().getTime() < new Date(expiration).getTime();
  //   }
  //   return false;
  // }

  /**
   * Programa el refresco del token
   */
  private scheduleRefreshToken() {
    this.clearRefreshTokenTimer();
    
    // Obtiene la fecha de expiración del token
    const expiration = this.getAccessTokenExpiration();
    if (!expiration) return;

    // Obtiene la fecha actual y la fecha de expiración
    const now = new Date().getTime();
    const expires = new Date(expiration).getTime();
    
    // Obtiene el offset de refresco y el tiempo de vida del token
    const refreshOffset = this.REFRESH_OFFSET_MS;
    const tokenLifetime = expires - now;
    
    // Obtiene el timeout
    const timeout = expires - now - refreshOffset;

    const refreshTime = new Date(now + timeout);
    console.log(`${this.dateService.getDateNowConsole()}: Refresco token programado en ${Math.round(timeout / 1000 / 60)} minutos. A las ${refreshTime.toLocaleString()} siguiente refresco automatico.`);
    // Programa el refresco del token
    if (timeout > 0) {
      this.refreshTokenTimeout = setTimeout(() => {
        this.tryRefreshToken().subscribe({
          next: () => console.log(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - Token refrescado exitosamente`),
          error: () => {
            console.error(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - Fallo al refrescar token`);
            this.logout();
          },
          complete: () => console.log(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - Completado Refresco Token`)
        })
      }, timeout);
      // this.inflightRefresh$ = of({
      //   access_token: this.getAccessToken(),
      //   refresh_token: this.getRefreshToken(),
      //   access_token_expiration: this.getAccessTokenExpiration()
      // })
    }else if(tokenLifetime > 0) { // EN ESTE MOMENTO EL TOKEN EXPIRA PRONTO
      console.log(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - else if - Token expirado`, tokenLifetime);
      // SE INTENTA REFRESCAR EL TOKEN
      this.tryRefreshToken().subscribe({
        next: () => console.log(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - else if - Token refrescado inmediatamente al detectar expiración/próxima expiración`),
        error: () => {
          console.error(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - else if - Fallo al refrescar token inmediatamente`);
          this.logout();
        },
        complete: () => console.log(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - else if - Completado Refresco Token Inmediato`)
      })
    }else{
      console.log(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - else - Token expirado`);
      this.tryRefreshToken().subscribe({
        next: () => console.log(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - else - Token refrescado inmediatamente al detectar expiración/próxima expiración`),
        error: () => {
          console.error(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - else - Fallo al refrescar token inmediatamente`);
          this.logout();
        },
        complete: () => console.log(`${this.dateService.getDateNowConsole()}: scheduleRefreshToken - else - Completado Refresco Token Inmediato`)
      })
    }
  }

  /**
   * Limpia el timer de refresco del token
   */
  private clearRefreshTokenTimer() {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
      this.refreshTokenTimeout = null;
    }
  }

  /**
   * Obtiene la fecha de expiración del token
   * @returns fecha de expiración del token
   */
  private getAccessTokenExpiration(): string | null {
    const expiration = localStorage.getItem(StorageVariable.TOKEN_EXPIRATION_KEY);
    return expiration ? expiration : null;
  }

  /**
   * Obtiene la IP del usuario
   */
  private obtenerIP(): void {
    this.http.get('https://api.ipify.org?format=json').subscribe((response: any) => {
      const ip = response.ip;
      sessionStorage.setItem(StorageVariable.IP, ip);
    });
  }

  /**
   * Indica si estamos dentro de la ventana de refresco (o expirado).
   * Se considera "dentro" cuando now >= (exp - REFRESH_OFFSET_MS),
   * para evitar refrescar continuamente fuera de tiempo.
   */
  private isWithinRefreshWindow(): boolean {
    const expiration = this.getAccessTokenExpiration();
    if (!expiration) return false;
    const now = Date.now();
    const exp = new Date(expiration).getTime();
    return now >= (exp - this.REFRESH_OFFSET_MS);
  }

  /**
   * Almacena los tokens en el localStorage
   * @param tokenData datos del token
   */
  private storeTokens(tokenData: Login): void {
    if(tokenData.access_token){
      sessionStorage.setItem(StorageVariable.ACCESS_TOKEN_KEY, tokenData.access_token);
    }
    if(tokenData.refresh_token){
      sessionStorage.setItem(StorageVariable.REFRESH_TOKEN_KEY, tokenData.refresh_token);
    }
    if(tokenData.access_token_expiration){
      localStorage.setItem(StorageVariable.TOKEN_EXPIRATION_KEY, tokenData.access_token_expiration);
    }
  }

  /**
   * Almacena la IP y el user agent en el sessionStorage
   */
  private storeDataClient(): void {
    if(this.getIp()){
      sessionStorage.setItem(StorageVariable.IP, this.getIp());
    }else{
      this.obtenerIP();
    }
    sessionStorage.setItem(StorageVariable.USER_AGENT, this.getUserAgent());
  }

  /**
   * Limpia la sesión
   */
  private clearSessionData() {
    console.trace(`${this.dateService.getDateNowConsole()}: clearSessionData - BORRANDO SESSION DATA`);
    sessionStorage.removeItem(StorageVariable.ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(StorageVariable.REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(StorageVariable.IP);
    sessionStorage.removeItem(StorageVariable.USER_AGENT);
    localStorage.removeItem(StorageVariable.TOKEN_EXPIRATION_KEY);
    this.currentUserRut = null;
    localStorage.removeItem(StorageVariable.RUT_USUARIO);
    localStorage.removeItem(StorageVariable.RUT_EMPRESA);
    localStorage.removeItem(StorageVariable.SUCURSAL_CODIGO);
    this.obtenerIP();
  }

  // CODIGO ANTERIOR
  // public urlUsuarioIntentaAcceder = '';
  // changeLoginStatusSubject = new Subject<boolean>();
  // changeLoginStatus$ = this.changeLoginStatusSubject.asObservable();
  // constructor(
  //   private bloqueoVistaService: BloqueoModuloService,
  //   private client: GraphQLClient,
  // ) { }

  // login(token: Login) {
  //   localStorage.setItem('token', token.access_token);
  //   localStorage.setItem('refresh_token', token.refresh_token);
  //   localStorage.setItem('access_token_expiration', token.access_token_expiration);
  //   this.changeLoginStatusSubject.next(true);
  // }

  // logout() {
  //   this.cerrarSesion();
  //   sessionStorage.removeItem('token');
  //   localStorage.removeItem('refresh_token');
  //   localStorage.removeItem('access_token_expiration');
  //   this.bloqueoVistaService.liberarTodosLosBloqueosDelUsuarioActual().subscribe();
  //   this.changeLoginStatusSubject.next(false);
  //   localStorage.removeItem('rut_empresa');
  //   localStorage.removeItem('rut_usuario');
  //   localStorage.removeItem('sucursal_codigo');
  //   // localStorage.removeItem('token');
  // }

  // isLoggedIn(url: string) {
  //   const tokeSession = localStorage.getItem('token');
  //   if(!tokeSession){
  //     this.urlUsuarioIntentaAcceder = url;
  //     localStorage.removeItem('rut_empresa');
  //     localStorage.removeItem('rut_usuario');
  //     localStorage.removeItem('sucursal_codigo');
  //     localStorage.removeItem('token');
  //     return false;
  //   }
  //   if (!isLogged) {
  //     this.urlUsuarioIntentaAcceder = url;
  //     localStorage.removeItem('rut_empresa');
  //     localStorage.removeItem('rut_usuario');
  //     localStorage.removeItem('sucursal_codigo');
  //     localStorage.removeItem('token');
  //     return false;
  //   }
  //   return true;
  // }

  // private cerrarSesion(): void {
  //   const inputParams = {
  //     rut_usuario: Number(localStorage.getItem('rut_usuario')),
  //     refresh_token: localStorage.getItem('refresh_token'),
  //   }
  //   console.log('CERRANDO SESION', inputParams);
  //   this.client.query({
  //     query: gql`
  //     query ($rut_usuario:Decimal, $refresh_token:String)
  //     {
  //       login
  //       {
  //         cerrarSesion(rut_usuario:$rut_usuario, refresh_token: $refresh_token)
  //         {
  //           mensajes
  //           {
  //             codigo
  //             contexto
  //             texto
  //             tipo
  //           }
  //           respuesta 
  //           {
  //             access_token
  //             refresh_token
  //             access_token_expiration
  //           }
  //         }
  //       }
  //     }
  //     `,
  //     variables: inputParams
  //   }, ({respuesta}) => {
  //     if (respuesta) {
  //       console.log('SE BORRO SESION ACTIVA');
  //     }else{
  //       console.warn('NO SE BORRO SESION ACTIVA: YA QUE NO EXISTIA O HUBO UN ERROR');
  //     }
  //   })
  // }

  // private hasValidToken(): boolean {
  //   const token = this.getAccessToken();
  //   const expiration = this.getAccessTokenExpiration();
  //   if (token && expiration) {
  //     return new Date().getTime() < new Date(expiration).getTime();
  //   }
  //   return false;
  // }
}